import React from 'react';
import {
  findKpiForYear, buildKpiTrail, latestReading, reverseReading, describeFreshness,
  describeReadingProblem,
  readingStateLabel,
} from '../../utils/meterFilters';
import '../../styles/MeterHero.css';

/**
 * Formats a reading for display, or reports that none has arrived.
 * @param {number|null} value - The reading.
 * @returns {string} A localised number, or a dash.
 */
const reading = (value) =>
  value == null ? '—' : value.toLocaleString(undefined, { maximumFractionDigits: 1 });

/**
 * One fact beside the headline reading.
 * @param {string} label - What the figure is.
 * @param {boolean} good - Tints the value, for freshness.
 * @param {React.ReactNode} children - The figure.
 */
const Aside = ({ label, good = false, children }) => (
  <div className="im-hero-aside-item">
    <b className={good ? 'ok' : undefined}>{children}</b>
    <span>{label}</span>
  </div>
);

/**
 * The top of the meter details page: what this meter reads right now, whether
 * it is still reporting, and which KPI its readings land against.
 *
 * The reading was previously only visible on the meters list, so answering
 * "what does it say?" meant going back a page.
 *
 * @param {object} meter - The meter, including its live flow state.
 * @param {string} currentFY - Financial year whose KPI mapping is shown.
 */
const MeterHero = ({ meter, currentFY }) => {
  const kpi = findKpiForYear(meter, currentFY);
  const trail = buildKpiTrail(kpi);
  const forward = latestReading(meter);
  const reverse = reverseReading(meter);
  const freshness = describeFreshness(meter);
  const problem = describeReadingProblem(meter);
  const net = forward == null ? null : forward - Math.abs(reverse || 0);

  const state = !kpi ? 'nokpi' : meter.isActive ? 'active' : 'inactive';

  return (
    <section className={`im-hero im-hero--${state}`}>
      <div className="im-hero-top">
        <h1>{meter.meterName}</h1>
        <span className={`im-hero-pill im-hero-pill--${meter.isActive ? 'active' : 'inactive'}`}>
          <span className="im-hero-dot" />
          {meter.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="im-hero-body">
        <div className="im-hero-reading">
          <span className="im-hero-value">{reading(forward)}</span>
          <span className="im-hero-unit">{meter.unit}</span>
          {problem && <span className="im-hero-flag">{problem.label}</span>}
          <span className="im-hero-caption">Forward totalizer</span>
        </div>

        <div className="im-hero-aside">
          <Aside label="Last reported" good={freshness.tone === 'ok'}>{freshness.label}</Aside>
          <Aside label="Reading state">{readingStateLabel(meter.readingState)}</Aside>
          {reverse ? (
            <Aside label="Reverse flow">{reading(Math.abs(reverse))} {meter.unit}</Aside>
          ) : null}
          {reverse ? (
            <Aside label="Net of reverse">{reading(net)} {meter.unit}</Aside>
          ) : null}
        </div>
      </div>

      {trail.length ? (
        <div className="im-hero-trail">
          {trail.map((step, index) => (
            <React.Fragment key={step}>
              {index > 0 && <span className="im-hero-sep">›</span>}
              <span className={`im-hero-step${index === trail.length - 1 ? ' im-hero-step--last' : ''}`}>
                {step}
              </span>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="im-hero-trail">
          <span className="im-hero-step im-hero-step--warn">
            No KPI mapped for {currentFY} — this meter records nothing
          </span>
        </div>
      )}
    </section>
  );
};

export default MeterHero;
